// Vollea — Home dashboard (player view + role badge)
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoTrophy, IoChevronForward, IoCalendar, IoArrowForward, IoGitNetwork, IoEnter, IoGrid, IoChatbubblesOutline, IoTimeOutline, IoBusinessOutline } from "react-icons/io5";
import { C, SP, R, ROLE_LABELS } from "../lib/theme";
import { Card, Avatar, Logo, Btn, SectionLabel } from "../components/ui-vollea";
import NotificationBell from "../components/NotificationBell";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

const dateLabel = (iso) => {
  if (!iso) return "Date TBC";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Date TBC";
  return d.toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};
const isOverdue = (iso) => { if (!iso) return false; const d = new Date(iso); return !isNaN(d.getTime()) && d.getTime() < Date.now(); };

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const isAdmin = user?.role === "club_admin" || user?.role === "league_organiser";
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get("/profile/me");
        setProfile(p);
        try { const lg = await api.get("/leagues?mine=true"); setLeagues(lg || []); } catch {}
        try {
          const my = await api.get(`/matches?player_id=${user?.user_id}`);
          setUpcoming((my || []).filter((m) => m.status === "scheduled"));
        } catch {}
      } catch {}
    })();
  }, [user]);

  const sectionTitle = { color: C.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 1.2, marginBottom: SP.md, textTransform: "uppercase" };
  const section = { padding: `0 ${SP.lg}px`, marginTop: SP.xl };

  return (
    <div className="vollea-fade" style={{ background: C.bg, paddingTop: 12, paddingBottom: 90, minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: `0 ${SP.lg}px`, marginBottom: SP.lg }}>
        <div>
          <div style={{ color: C.textMuted, fontSize: 14 }}>Welcome back</div>
          <div data-testid="dashboard-user-name" style={{ color: C.text, fontWeight: 700, fontSize: 26, marginTop: 2 }}>{user?.name}</div>
          <div style={{ background: C.limeTint, display: "inline-block", padding: `4px ${SP.md}px`, borderRadius: R.pill, marginTop: 6 }}>
            <span style={{ color: C.lime, fontSize: 11, fontWeight: 600 }}>{ROLE_LABELS[user?.role || "player"]}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
          {isSuper && <NotificationBell testid="dashboard-notification-bell" />}
          <Logo size={13} />
        </div>
      </div>

      {/* Rank card */}
      <div style={{ padding: `0 ${SP.lg}px` }}>
        <Card style={{ background: C.surface2 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar uri={profile?.photo} name={user?.name} size={56} />
            <div style={{ marginLeft: SP.md, flex: 1, minWidth: 0 }}>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 1 }}>GLOBAL RANK</div>
              <div data-testid="dashboard-rank" style={{ color: C.text, fontWeight: 700, fontSize: 28 }}>#{profile?.current_ranking || "—"}</div>
            </div>
            <div style={{ textAlign: "center", marginLeft: SP.lg }}>
              <div data-testid="dashboard-points" style={{ color: C.text, fontWeight: 700, fontSize: 20 }}>{profile?.points || 0}</div>
              <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>PTS</div>
            </div>
            <div style={{ textAlign: "center", marginLeft: SP.lg }}>
              <div data-testid="dashboard-winrate" style={{ color: C.lime, fontWeight: 700, fontSize: 20 }}>{profile?.win_percentage || 0}%</div>
              <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 600, letterSpacing: 1 }}>WIN</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Upcoming */}
      <div style={section}>
        <div style={sectionTitle}>Upcoming Matches</div>
        {upcoming.length > 0 ? upcoming.slice(0, 4).map((m) => {
          const due = isOverdue(m.scheduled_at);
          return (
            <Card key={m.match_id} style={{ marginBottom: SP.sm, border: `1px solid ${due ? C.warning : C.border}`, cursor: "pointer" }} onClick={() => navigate(`/matches/${m.match_id}`)} testid={`dashboard-upcoming-${m.match_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: SP.md }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, background: C.surface2, padding: "5px 8px", borderRadius: R.sm }}>
                  <IoCalendar size={13} color={m.scheduled_at ? C.lime : C.textDim} />
                  <span style={{ color: C.lime, fontSize: 11, fontWeight: 700 }}>{dateLabel(m.scheduled_at)}</span>
                </div>
                <span style={{ color: C.lime, fontSize: 12, fontWeight: 600 }}>{m.league_name}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: C.text, fontWeight: 600, fontSize: 15, flex: 1 }}>{m.team_a_players?.map((p) => p.name.split(" ")[0]).join(" & ")}</span>
                <span style={{ color: C.textDim, fontWeight: 700, fontSize: 13, margin: `0 ${SP.md}px` }}>VS</span>
                <span style={{ color: C.text, fontWeight: 600, fontSize: 15, flex: 1, textAlign: "right" }}>{m.team_b_players?.map((p) => p.name.split(" ")[0]).join(" & ")}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: SP.md, paddingTop: SP.md, borderTop: `1px solid ${C.divider}` }}>
                <span style={{ color: due ? C.warning : C.lime, fontWeight: 700, fontSize: 12, letterSpacing: 1 }}>{due ? "SUBMIT RESULT NOW" : "SUBMIT RESULT"}</span>
                <IoArrowForward size={15} color={due ? C.warning : C.lime} />
              </div>
            </Card>
          );
        }) : <Card><span style={{ color: C.textMuted, fontSize: 13 }}>No upcoming matches scheduled.</span></Card>}
      </div>

      {/* Quick actions */}
      <div style={section}>
        <SectionLabel>Quick Actions</SectionLabel>
        <div style={{ display: "flex", flexWrap: "wrap", gap: SP.md }}>
          {(isAdmin || isSuper) && <Action icon={<IoGitNetwork size={26} color={C.lime} />} label="Fixtures" onClick={() => navigate("/leagues")} testid="qa-fixtures" />}
          {(isAdmin || isSuper) && <Action icon={<IoTimeOutline size={26} color={C.lime} />} label="Results Outstanding" onClick={() => navigate("/outstanding")} testid="qa-outstanding" />}
          {(user?.role === "club_admin" || isSuper) && <Action icon={<IoBusinessOutline size={26} color={C.lime} />} label="My Club" onClick={() => navigate("/my-club")} testid="qa-my-club" />}
          {(user?.role === "league_organiser" || isSuper) && <Action icon={<IoBusinessOutline size={26} color={C.lime} />} label="My Clubs" onClick={() => navigate("/organiser/clubs")} testid="qa-organiser-clubs" />}
          <Action icon={<IoEnter size={26} color={C.lime} />} label="Join League" onClick={() => navigate("/leagues")} testid="qa-join-league" />
          {isSuper && <Action icon={<IoGrid size={26} color={C.lime} />} label="Super Admin" onClick={() => navigate("/admin")} testid="qa-admin" />}
          <Action icon={<IoChatbubblesOutline size={26} color={C.lime} />} label="Beta Feedback" onClick={() => navigate("/feedback")} testid="qa-feedback" />
        </div>
      </div>

      {/* My leagues */}
      <div style={section}>
        <SectionLabel>My Leagues</SectionLabel>
        {leagues.length === 0 ? (
          <Card>
            <span style={{ color: C.textMuted, fontSize: 13 }}>You haven't joined any leagues yet.</span>
            <div style={{ marginTop: SP.md }}><Btn label="BROWSE LEAGUES" onClick={() => navigate("/leagues")} style={{ width: 200 }} testid="browse-leagues-cta" /></div>
          </Card>
        ) : leagues.map((l) => (
          <Card key={l.league_id} style={{ marginBottom: SP.sm }} onClick={() => navigate(`/leagues/${l.league_id}`)} testid={`my-league-${l.league_id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
              <div style={{ width: 38, height: 38, borderRadius: R.md, background: C.limeTint, display: "flex", alignItems: "center", justifyContent: "center" }}><IoTrophy size={18} color={C.lime} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{l.name}</div>
                <div style={{ color: C.textMuted, fontSize: 13 }}>{l.member_count ?? 0} players · {l.divisions?.length ?? 0} divisions</div>
              </div>
              <IoChevronForward size={20} color={C.textDim} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Action({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ width: "47%", flexGrow: 1, background: C.surface, borderRadius: R.lg, border: `1px solid ${C.border}`, padding: SP.lg, display: "flex", flexDirection: "column", gap: SP.sm, alignItems: "flex-start" }}>
      {icon}
      <span style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{label}</span>
    </button>
  );
}
